import TractDetailData from 'src/models/tract_detail/TractDetail';
import { Reducer, Action } from 'redux';
import { IRootState } from './index';
import { call, all, select, takeEvery, fork, put, delay } from 'redux-saga/effects';

import { selectors as authSelectors } from 'src/reducers/Auth';
import { actionCreators as layoutActionCreators } from 'src/reducers/Layout';
import { actionCreators as tagActionCreators } from 'src/reducers/Tag';

import * as _ from 'lodash';

import Api from '../api';
import Meridian from '../models/Meridian';
import Township from '../models/Township';
import Section from 'src/models/Section';
import TractDetailFormData from '../models/tract_detail/TractDetailFormData';
import Ownership from '../models/tract_detail/Ownership';
import ExaminedHistoryChanges from 'src/models/tract_detail/ExaminedHistoryChanges';
import AddOwnershipCollection from 'src/models/tract_detail/add_ownership_collection/AddOwnershipCollection';
import NewTract from 'src/models/tract_detail/NewTract';
import { replace } from 'connected-react-router';
import SearchResult from 'src/models/OwnerSearchResult';
import { Formation, HorizonInterval } from 'src/models/Horizon';
import { apiCall } from './SagaUtil';
import { ODataOptions } from 'src/util/ODataBuilder';
import PagedResultSet from 'src/models/common/PagedResultSet';
import AddOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddOwnership';
import CreateHorizonDto from 'src/models/tract_detail/CreateHorizonDto';
import Horizon from 'src/models/tract_detail/Horizon';
import Owner from 'src/models/tract_detail/Owner';
import TractDetail from 'src/models/tract_detail/TractDetail';
import ErrorResponse from '../models/ErrorResponse';
import TractHorizonSummary from 'src/models/tractHorizonSummary';
import { ITableConfig } from 'src/components/common/table';
import Tract from 'src/models/Tract';
import USPSCityState from 'src/models/USPSCityState';
import EasternTownship from 'src/models/EasternTownship';
import EasternMap from 'src/models/EasternMap';
import SurfaceTract from 'src/models/SurfaceTract';

// Store
export interface ITractState {
    easternMap: EasternMap;
    uspsCityState: USPSCityState;
    selectedTownship: Township;
    selectedMap: EasternMap;
    selectedSection: Section;
    meridians: Array<Meridian>;
    townships: Array<Township>;
    easternTownships: Array<EasternTownship>;
    township: Township;
    easternTownship: EasternTownship;
    sections: Array<Section>;
    easternMaps: Array<EasternMap>;
    townshipNumber: number;
    townshipCardinal: string;
    rangeNumber: number;
    rangeCardinal: string;
    tractDetail: TractDetailData | undefined;
    ownershipDetail: Ownership;
    isTractLoading: boolean;
    isCloneTractLoading: boolean;
    isSectionLoading: boolean;
    isTownshipLoading: boolean;
    isHorizonLoading: boolean;
    isLoadingSearchOwners: boolean;
    tracts: Array<TractHorizonSummary>;
    ownerTracts: Array<Tract>;
    ownerSurfaceTracts: Array<SurfaceTract>;
    ownerships: PagedResultSet<Ownership>;
    cloneTracts: Array<TractDetail>;
    cloneSections: Array<Section>;
    cloneTownships: Array<Township>;
    cloneTract: TractDetail;
    isSectionsLoading: boolean;
    isTractsLoading: boolean;
    isTownshipsLoading: boolean;
    isMeridiansLoading: boolean;
    isOwnershipDetailLoading: boolean;
    pagedSearchOwnersResults: PagedResultSet<SearchResult>;
    searchOwnersResults: Array<SearchResult>;
    searchOwnersDeviationsResults: Array<SearchResult>;
    formations: Array<Formation>;
    uniqueHorizonIntervals: Array<HorizonInterval>;
    error: string;
    ownershipPageConfig: ITableConfig;
    ownersPageConfig: ITableConfig;
}

const initialState: ITractState = {
    uspsCityState: undefined,
    selectedSection: undefined,
    selectedTownship: undefined,
    selectedMap: undefined,
    meridians: undefined,
    townships: undefined,
    easternTownships: undefined,
    township: undefined,
    easternTownship: undefined,
    sections: undefined,
    easternMaps: [],
    easternMap: undefined,
    townshipNumber: undefined,
    townshipCardinal: undefined,
    rangeNumber: undefined,
    rangeCardinal: undefined,
    tracts: undefined,
    ownerTracts: undefined,
    ownerSurfaceTracts: undefined,
    ownerships: undefined,
    cloneTracts: undefined,
    cloneSections: undefined,
    cloneTownships: undefined,
    cloneTract: undefined,
    isTractLoading: false,
    isCloneTractLoading: false,
    isSectionLoading: false,
    isTownshipLoading: false,
    isHorizonLoading: false,
    isSectionsLoading: false,
    isTractsLoading: false,
    isTownshipsLoading: false,
    isMeridiansLoading: false,
    isOwnershipDetailLoading: false,
    tractDetail: new TractDetailData(),
    ownershipDetail: undefined,
    pagedSearchOwnersResults: undefined,
    searchOwnersResults: undefined,
    searchOwnersDeviationsResults: undefined,
    isLoadingSearchOwners: false,
    formations: [],
    uniqueHorizonIntervals: [],
    error: undefined,
    ownershipPageConfig: {
        rowCount: 25,
        pageNumber: 1,
    },
    ownersPageConfig: {
        rowCount: 25,
        pageNumber: 1
    }
};

// Action Types
export enum actionTypes {
    GET_CITY_STATE = '[tract] GET_CITY_STATE',
    GET_CITY_STATE_SUCCESS = '[tract] GET_CITY_STATE_SUCCESS',
    GET_CITY_STATE_ERROR = '[tract] GET_CITY_STATE_ERROR',
    INIT_OWNER_TRACTS = '[tract] INIT_OWNER_TRACTS',
    GET_TRACT_DETAIL = '[tract] GET_TRACT_DETAIL',
    GET_TRACT_DETAIL_SUCCESS = '[tract] GET_TRACT_DETAIL_SUCCESS',
    GET_CLONE_TRACT_DETAIL = '[tract] GET_CLONE_TRACT_DETAIL',
    GET_CLONE_TRACT_DETAIL_SUCCESS = '[tract] GET_CLONE_TRACT_DETAIL_SUCCESS',
    SAVE_TRACT_DETAIL = '[tract] SAVE_TRACT_DETAIL',
    SAVE_TRACT_DETAIL_SUCCESS = '[tract] SAVE_TRACT_DETAIL_SUCCESS',
    GET_SECTION = '[tract] GET_SECTION',
    GET_SECTION_SUCCESS = '[tract] GET_SECTION_SUCCESS',
    GET_TOWNSHIP = '[tract] GET_TOWNSHIP',
    GET_TOWNSHIP_SUCCESS = '[tract] GET_TOWNSHIP_SUCCESS',
    GET_TOWNSHIPS = '[tract] GET_TOWNSHIP',
    GET_TOWNSHIPS_SUCCESS = '[tract] GET_TOWNSHIP_SUCCESS',
    GET_EASTERN_TOWNSHIPS = '[tract] GET_EASTERN_TOWNSHIPS',
    GET_EASTERN_TOWNSHIPS_SUCCESS = '[tract] GET_EASTERN_TOWNSHIPS_SUCCESS',
    GET_SECTIONS = '[tract] GET_SECTIONS',
    GET_SECTIONS_SUCCESS = '[tract] GET_SECTIONS_SUCCESS',
    GET_EASTERN_MAP = '[tract] GET_EASTERN_MAP',
    GET_EASTERN_MAP_SUCCESS = '[tract] GET_EASTERN_MAP_SUCCESS',
    GET_MERIDIANS = '[tract] GET_MERIDIANS',
    GET_MERIDIANS_SUCCESS = '[tract] GET_MERIDIANS_SUCCESS',
    SELECT_TOWNSHIP = '[tract] SELECT_TOWNSHIP',
    SELECT_MAP = '[tract] SELECT_MAP',
    SELECT_SECTION = '[tract] SELECT_SECTION',
    CREATE_TOWNSHIP = '[tract] CREATE_TOWNSHIP',
    CREATE_TOWNSHIP_SUCCESS = '[tract] CREATE_TOWNSHIP_SUCCESS',
    CREATE_EASTERN_TOWNSHIP = '[tract] CREATE_EASTERN_TOWNSHIP',
    CREATE_EASTERN_TOWNSHIP_SUCCESS = '[tract] CREATE_EASTERN_TOWNSHIP_SUCCESS',
    CREATE_EASTERN_MAP = '[tract] CREATE_EASTERN_MAP',
    CREATE_EASTERN_MAP_SUCCESS = '[tract] CREATE_EASTERN_MAP_SUCCESS',
    CREATE_TOWNSHIP_ERROR = '[tract] CREATE_TOWNSHIP_ERROR',
    UPDATE_TOWNSHIP = '[tract] UPDATE_TOWNSHIP',
    UPDATE_TOWNSHIP_SUCCESS = '[tract] UPDATE_TOWNSHIP_SUCCESS',
    CREATE_SECTION = '[tract] CREATE_SECTION',
    CREATE_SECTION_SUCCESS = '[tract] CREATE_SECTION_SUCCESS',
    UPDATE_SECTION = '[tract] UPDATE_SECTION',
    UPDATE_SECTION_SUCCESS = '[tract] UPDATE_SECTION_SUCCESS',
    GET_OWNERSHIP_DETAIL = '[tract] GET_OWNERSHIP_DETAIL',
    GET_OWNERSHIP_DETAIL_SUCCESS = '[tract] GET_OWNERSHIP_DETAIL_SUCCESS',
    SAVE_OWNERSHIP_DETAIL = '[tract] SAVE_OWNERSHIP_DETAIL',
    SAVE_OWNERSHIP_DETAIL_SUCCESS = '[tract] SAVE_OWNERSHIP_DETAIL_SUCCESS',
    GET_TRACTS = '[tract] GET_TRACTS',
    GET_TRACTS_SUCCESS = '[tract] GET_TRACT_SUCCESS',
    GET_CLONE_TRACTS = '[tract] GET_CLONE_TRACTS',
    GET_CLONE_TRACTS_SUCCESS = '[tract] GET_CLONE_TRACTS_SUCCESS',
    GET_CLONE_SECTIONS = '[tract] GET_SECTIONS',
    GET_CLONE_SECTIONS_SUCCESS = '[tract] GET_CLONE_SECTIONS_SUCCESS',
    GET_CLONE_TOWNSHIPS = '[tract] GET_CLONE_TOWNSHIPS',
    GET_CLONE_TOWNSHIPS_SUCCESS = '[tract] GET_CLONE_TOWNSHIPS_SUCCESS',
    CREATE_HORIZON = '[tract] CREATE_HORIZON',
    CREATE_HORIZON_SUCCESS = '[tract] CREATE_HORIZON_SUCCESS',
    UPDATE_TRACT_NOTE = '[tract] UPDATE_TRACT_NOTE',
    UPDATE_TRACT_NOTE_SUCCESS = '[tract] UPDATE_TRACT_NOTE_SUCCESS',
    UPDATE_TRACT_NOTE_ERROR = '[tract] UPDATE_TRACT_NOTE_ERROR',
    UPDATE_OWNER_NOTE = '[tract] UPDATE_OWNER_NOTE',
    UPDATE_OWNER_NOTE_SUCCESS = '[tract] UPDATE_OWNER_NOTE_SUCCESS',
    UPDATE_OWNER_NOTE_ERROR = '[tract] UPDATE_OWNER_NOTE_ERROR',
    UPDATE_HORIZON_INTERVAL = '[tract] UPDATE_HORIZON_INTERVAL',
    UPDATE_HORIZON_SUCCESS = '[tract] UPDATE_HORIZON_SUCCESS',
    SAVE_HORIZON_EXAMINED_HISTORY = '[tract] SAVE_HORIZON_EXAMINED_HISTORY',
    DELETE_OWNERSHIP_DETAIL = '[tract] DELETE_OWNERSHIP_DETAIL',
    SAVE_ADD_OWNERSHIP_COLLECTION = '[tract] SAVE_ADD_OWNERSHIP_COLLECTION',
    CREATE_TRACT = '[tract] CREATE_TRACT',
    CREATE_TRACT_SUCCESS = '[tract] CREATE_TRACT_SUCCESS',
    GET_SEARCH_OWNERS = '[tract] GET_SEARCH_OWNERS',
    GET_SEARCH_OWNERS_SUCCESS = '[tract] GET_SEARCH_OWNERS_SUCCESS',
    CLEAR_SEARCH_OWNERS_RESULTS = '[tract] CLEAR_SEARCH_OWNERS_RESULTS',
    GET_FORMATIONS = '[tract] GET_FORMATIONS',
    GET_FORMATIONS_SUCCESS = '[tract] GET_FORMATIONS_SUCCESS',
    GET_PAGED_OWNERSHIPS = '[tract] GET_PAGED_OWNERSHIPS',
    GET_PAGED_OWNERSHIPS_SUCCESS = '[tract] GET_PAGED_OWNERSHIPS_SUCCESS',
    CLEAR_TRACT_DETAIL = '[tract] CLEAR_TRACT_DETAIL',
    SEQUENCE_HORIZONS = '[tract] SEQUENCE_HORIZONS',
    GET_UNIQUE_HORIZONS = '[tract] GET_UNIQUE_HORIZONS',
    GET_UNIQUE_HORIZONS_SUCCESS = '[tract] GET_UNIQUE_HORIZONS_SUCCESS',
    SAVE_ADD_OWNERSHIP = '[tract] SAVE_ADD_OWNERSHIP',
    SAVE_ADD_OWNERSHIP_SUCCESS = '[tract] SAVE_ADD_OWNERSHIP_SUCCESS',
    GET_TRACT_OWNERS = '[tract] GET_TRACT_OWNERS',
    GET_TRACT_OWNERS_SUCCESS = '[tract] GET_TRACT_OWNERS_SUCCESS',
    GET_TOWNSHIP_SECTIONS_FOR_SECTION = '[tract] GET_TOWNSHIP_SECTIONS_FOR_SECTION',
    GET_TOWNSHIP_SECTIONS_FOR_SECTION_SUCCESS = '[tract] GET_TOWNSHIP_SECTIONS_FOR_SECTION_SUCCESS',
    CREATE_FORMATION = '[tract] CREATE_FORMATION',
    CREATE_FORMATION_SUCCESS = '[tract] CREATE_FORMATION_SUCCESS',
    DELETE_OWNERSHIP = '[tract] DELETE_OWNERSHIP',
    DELETE_TRACT = '[tract] DELETE_TRACT',
    DELETE_SECTION = '[tract] DELETE_SECTION',
    DELETE_SECTION_SUCCESS = '[tract] DELETE_SECTION_SUCCESS',
    DELETE_SECTION_ERROR = '[tract] DELETE_SECTION_ERROR',
    DELETE_TOWNSHIP = '[tract] DELETE_TOWNSHIP',
    DELETE_TOWNSHIP_SUCCESS = '[tract] DELETE_TOWNSHIP_SUCCESS',
    DELETE_TOWNSHIP_ERROR = '[tract] DELETE_TOWNSHIP_ERROR',
    DELETE_HORIZON = '[tract] DELETE_HORIZON',
    DELETE_HORIZON_SUCCESS = '[tract] DELETE_HORIZON_SUCCESS',
    DELETE_HORIZON_ERROR = '[tract] DELETE_HORIZON_ERROR',
    INIT_TRACT_PAGE = '[tract] INIT_TRACT_PAGE',
    INIT_OWNER_LIST = '[tract] INIT_OWNERSHIP_LIST',
    CLEAR_ERROR = '[tract] CLEAR_ERROR',
    INCREMENT_OWNERSHIP_PAGE = '[tract] INCREMENT_OWNERSHIP_PAGE',
    INCREMENT_OWNERSHIP_SUCCESS = '[tract] INCREMENT_OWNERSHIP_SUCCESS',
    INCREMENT_OWNERS_PAGE = '[tract] INCREMENT_OWNERS_PAGE',
    INCREMENT_OWNERS_PAGE_SUCCESS = '[tract] INCREMENT_OWNERS_PAGE_SUCCESS',
    PROMOTE_ADDITIONAL_OWNERSHIP = '[tract] PROMOTE_ADDITIONAL_OWNERSHIP',
    CLEAR_SEARCH_RESULTS = '[tract] CLEAR_SEARCH_RESULTS',
    GET_TRACTS_BY_OWNER = '[tract] GET_TRACTS_BY_OWNER',
    GET_TRACTS_BY_OWNER_SUCCESS = '[tract] GET_TRACTS_BY_OWNER_SUCCESS',
    GET_SURFACE_TRACTS_BY_OWNER = '[tract] GET__SURFACE_TRACTS_BY_OWNER',
    GET_SURFACE_TRACTS_BY_OWNER_SUCCESS = '[tract] GET_SURFACE_TRACTS_BY_OWNER_SUCCESS',
    GET_SEARCH_OWNERS_DEVIATIONS = '[tract] GET_SEARCH_OWNERS_DEVIATIONS',
    GET_SEARCH_OWNERS_DEVIATIONS_SUCCESS = '[tract] GET_SEARCH_OWNERS_DEVIATIONS_SUCCESS',
    GET_SEARCH_PAGED_OWNERS = '[tract] GET_SEARCH_PAGED_OWNERS',
    GET_SEARCH_PAGED_OWNERS_SUCCESS = '[tract] GET_SEARCH_PAGED_OWNERS_SUCCESS',
    INIT_OWNERS_PAGE = '[tract] INIT_OWNERS_PAGE',
    REGISTER_OWNERSHIPS = '[tract] REGISTER_OWNERSHIPS',
    GET_EASTERN_TOWNSHIP_SUCCESS = '[tract] GET_EASTERN_TOWNSHIP_SUCCESS',
    GET_EASTERN_TOWNSHIP = '[tract] GET_EASTERN_TOWNSHIP'
}

// Action Creator
export const actionCreators = {
    getCityState: (zipCode: string) => {
        return { type: actionTypes.GET_CITY_STATE, payload: { zipCode } };
    },
    getCityStateSuccess: (result: USPSCityState) => {
        return { type: actionTypes.GET_CITY_STATE_SUCCESS, payload: result };
    },
    getCityStateError: (result: ErrorResponse) => {
        const { message } = result;
        return { type: actionTypes.GET_CITY_STATE_ERROR, payload: { message } };
    },
    initializeOwnerList: (config: ITableConfig) => {
        return {
            type: actionTypes.INIT_OWNER_LIST,
            payload: config
        };
    },
    initializeOwnerTracts: () => {
        return { type: actionTypes.INIT_OWNER_TRACTS };
    },
    clearSearchResults: () => {
        return { type: actionTypes.CLEAR_SEARCH_RESULTS };
    },
    getTractDetail: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        options: ODataOptions
    ) => {
        return {
            type: actionTypes.GET_TRACT_DETAIL,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                options,
            },
        };
    },
    getTractDetailSuccess: (tractDetail: TractDetailData) => {
        return { type: actionTypes.GET_TRACT_DETAIL_SUCCESS, payload: tractDetail };
    },
    incrementOwnershipPage: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number, horizonNumber: number) => {
        return {
            type: actionTypes.INCREMENT_OWNERSHIP_PAGE,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
            },
        };
    },
    incrementOwnersPage: (clientNumber: number, projectNumber: number, options: ODataOptions) => {
        return {
            type: actionTypes.INCREMENT_OWNERS_PAGE,
            payload: {
                clientNumber,
                projectNumber,
                options
            }
        };
    },
    incrementOwnershipSuccess: (ownershipList: PagedResultSet<Ownership>) => {
        return {
            type: actionTypes.INCREMENT_OWNERSHIP_SUCCESS,
            payload: ownershipList,
        };
    },
    incrementOwnersSuccess: (results: PagedResultSet<SearchResult>) => {
        return {
            type: actionTypes.INCREMENT_OWNERS_PAGE_SUCCESS,
            payload: results
        };
    },
    getCloneTractDetail: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number) => {
        return {
            type: actionTypes.GET_CLONE_TRACT_DETAIL,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                options: new ODataOptions(),
            },
        };
    },
    getCloneTractDetailSuccess: (tractDetail: TractDetailData) => {
        return {
            type: actionTypes.GET_CLONE_TRACT_DETAIL_SUCCESS,
            payload: tractDetail,
        };
    },
    getOwnershipDetail: (payload: { ownerId: number; horizonId: number; projectId: number; tractId: number }) => {
        return { type: actionTypes.GET_OWNERSHIP_DETAIL, payload };
    },
    getOwnershipDetailSuccess: (ownership: Ownership) => {
        return {
            type: actionTypes.GET_OWNERSHIP_DETAIL_SUCCESS,
            payload: ownership,
        };
    },
    getTracts: (clientNumber: number, projectNumber: number, sectionNumber: number, filter: string) => {
        return {
            type: actionTypes.GET_TRACTS,
            payload: { clientNumber, projectNumber, sectionNumber },
        };
    },
    getTractsSuccess: (tracts: Array<TractHorizonSummary>) => {
        return { type: actionTypes.GET_TRACTS_SUCCESS, payload: tracts };
    },
    getCloneTracts: (clientNumber: number, projectNumber: number, sectionNumber: number) => {
        return {
            type: actionTypes.GET_CLONE_TRACTS,
            payload: { clientNumber, projectNumber, sectionNumber },
        };
    },
    getCloneTractsSuccess: (tracts: Array<TractDetail>) => {
        return { type: actionTypes.GET_CLONE_TRACTS_SUCCESS, payload: tracts };
    },
    getCloneSections: (clientNumber: number, projectNumber: number, townshipId: number) => {
        return {
            type: actionTypes.GET_CLONE_SECTIONS,
            payload: { clientNumber, projectNumber, townshipId },
        };
    },
    getCloneSectionsSuccess: (sections: Array<Section>) => {
        return { type: actionTypes.GET_CLONE_SECTIONS_SUCCESS, payload: sections };
    },
    getCloneTownships: (clientNumber: number, projectNumber: number) => {
        return {
            type: actionTypes.GET_CLONE_TOWNSHIPS,
            payload: { clientNumber, projectNumber },
        };
    },
    getCloneTownshipsSuccess: (townships: Array<Township>) => {
        return {
            type: actionTypes.GET_CLONE_TOWNSHIPS_SUCCESS,
            payload: townships,
        };
    },
    getSection: (id: number) => {
        return { type: actionTypes.GET_SECTION, payload: id };
    },
    getSections: (clientNumber: number, projectNumber: number, townshipId: number) => {
        return {
            type: actionTypes.GET_SECTIONS,
            payload: { clientNumber, projectNumber, townshipId },
        };
    },
    getMap: (clientNumber: number, projectNumber: number, mapNumber: number) => {
        return {
            type: actionTypes.GET_EASTERN_MAP,
            payload: { clientNumber, projectNumber, mapNumber },
        };
    },
    getMapSuccess: (easternMap: EasternMap) => {
        return { type: actionTypes.GET_EASTERN_MAP_SUCCESS, payload: easternMap };
    },
    getSectionsSuccess: (sections: Array<Section>) => {
        return { type: actionTypes.GET_SECTIONS_SUCCESS, payload: sections };
    },
    getTownshipSectionsBySectionsSuccess: (payload: { sections: Array<Section>; townships: Array<Township> }) => {
        return {
            type: actionTypes.GET_TOWNSHIP_SECTIONS_FOR_SECTION_SUCCESS,
            payload: payload,
        };
    },
    getTownships: (clientNumber: number, projectNumber: number) => {
        return {
            type: actionTypes.GET_TOWNSHIPS,
            payload: { clientNumber, projectNumber },
        };
    },
    getTownshipsSuccess: (townships: Array<Township>) => {
        return { type: actionTypes.GET_TOWNSHIPS_SUCCESS, payload: townships };
    },
    getEasternTownships: (clientNumber: number, projectNumber: number) => {
        return {
            type: actionTypes.GET_EASTERN_TOWNSHIPS,
            payload: { clientNumber, projectNumber },
        };
    },
    getEasternTownshipsSuccess: (townships: Array<EasternTownship>) => {
        return {
            type: actionTypes.GET_EASTERN_TOWNSHIPS_SUCCESS,
            payload: townships,
        };
    },
    getTownship: (clientNumber: number, projectNumber: number, townshipId: number) => {
        return {
            type: actionTypes.GET_TOWNSHIP,
            payload: { clientNumber, projectNumber, townshipId },
        };
    },
    getTownshipSuccess: (township: Township) => {
        return {
            type: actionTypes.GET_TOWNSHIP_SUCCESS,
            payload: township
        };
    },
    getEasternTownship: (clientNumber: number, projectNumber: number, townshipId: number) => {
        return {
            type: actionTypes.GET_EASTERN_TOWNSHIP,
            payload: { clientNumber, projectNumber, townshipId },
        };
    },
    getEasternTownshipSuccess: (township: EasternTownship) => {
        return {
            type: actionTypes.GET_EASTERN_TOWNSHIP_SUCCESS,
            payload: township,
        };
    },
    getMeridians: (clientNumber: number, projectNumber: number) => {
        return {
            type: actionTypes.GET_MERIDIANS,
            payload: { clientNumber, projectNumber },
        };
    },
    getMeridiansSuccess: (meridians: Array<Meridian>) => {
        return { type: actionTypes.GET_MERIDIANS_SUCCESS, payload: meridians };
    },
    selectTownship: (clientNumber: number, projectNumber: number, township: Township) => {
        return {
            type: actionTypes.SELECT_TOWNSHIP,
            payload: { clientNumber, projectNumber, township },
        };
    },
    selectSection: (clientNumber: number, projectNumber: number, sectionNumber: number) => {
        return {
            type: actionTypes.SELECT_SECTION,
            payload: { clientNumber, projectNumber, sectionNumber },
        };
    },
    selectMap: (clientNumber: number, projectNumber: number, mapNumber: number) => {
        return {
            type: actionTypes.SELECT_MAP,
            payload: { clientNumber, projectNumber, mapNumber },
        };
    },
    clearError: () => {
        return { type: actionTypes.CLEAR_ERROR };
    },
    createTownship: (clientNumber: number, projectNumber: number, township: Township) => {
        return {
            type: actionTypes.CREATE_TOWNSHIP,
            payload: { clientNumber, projectNumber, township },
        };
    },
    createTownshipSuccess: (township: Township) => {
        return { type: actionTypes.CREATE_TOWNSHIP_SUCCESS, payload: township };
    },
    createEasternTownship: (clientNumber: number, projectNumber: number, township: EasternTownship) => {
        return {
            type: actionTypes.CREATE_EASTERN_TOWNSHIP,
            payload: { clientNumber, projectNumber, township },
        };
    },
    createEasternMap: (clientNumber: number, projectNumber: number, townshipId: number, map: EasternMap) => {
        return {
            type: actionTypes.CREATE_EASTERN_MAP,
            payload: { clientNumber, projectNumber, townshipId, map },
        };
    },
    createEasternMapSuccess: (map: EasternMap) => {
        return { type: actionTypes.CREATE_EASTERN_MAP_SUCCESS, payload: { map } };
    },
    createEasternTownshipSuccess: (township: EasternTownship) => {
        return {
            type: actionTypes.CREATE_EASTERN_TOWNSHIP_SUCCESS,
            payload: township,
        };
    },
    createTownshipFailure: (payload: ErrorResponse) => {
        return { type: actionTypes.CREATE_TOWNSHIP_ERROR, payload: payload };
    },
    updateTownship: (clientNumber: number, projectNumber: number, township: Township) => {
        return {
            type: actionTypes.UPDATE_TOWNSHIP,
            payload: { clientNumber, projectNumber, township },
        };
    },
    updateTownshipSuccess: (township: Township) => {
        return { type: actionTypes.UPDATE_TOWNSHIP_SUCCESS, payload: township };
    },
    createSection: (clientNumber: number, projectNumber: number, townshipId: number, section: Section) => {
        return {
            type: actionTypes.CREATE_SECTION,
            payload: { clientNumber, projectNumber, townshipId, section },
        };
    },
    createSectionSuccess: (townshipId: number, section: Section) => {
        return {
            type: actionTypes.CREATE_SECTION_SUCCESS,
            payload: { townshipId, section },
        };
    },
    updateSection: (clientNumber: number, projectNumber: number, townshipId: number, section: Section) => {
        return {
            type: actionTypes.UPDATE_SECTION,
            payload: { clientNumber, projectNumber, townshipId, section },
        };
    },
    updateSectionSuccess: (townshipId: number, section: Section) => {
        return {
            type: actionTypes.UPDATE_SECTION_SUCCESS,
            payload: { townshipId, section },
        };
    },
    saveTractDetail: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        formValues: TractDetailFormData
    ) => {
        return {
            type: actionTypes.SAVE_TRACT_DETAIL,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                formValues,
            },
        };
    },
    createHorizon: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number, horizon: CreateHorizonDto) => {
        return {
            type: actionTypes.CREATE_HORIZON,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizon,
            },
        };
    },
    createHorizonSuccess: (horizon: Horizon) => {
        return { type: actionTypes.CREATE_HORIZON_SUCCESS, payload: horizon };
    },
    updateHorizonInterval: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        horizon: HorizonInterval
    ) => {
        return {
            type: actionTypes.UPDATE_HORIZON_INTERVAL,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                horizon,
            },
        };
    },
    updateOwnerNote: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        ownership: Ownership
    ) => {
        return {
            type: actionTypes.UPDATE_OWNER_NOTE,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                ownership,
            },
        };
    },
    updateOwnerNoteSuccess: () => {
        return { type: actionTypes.UPDATE_OWNER_NOTE_SUCCESS };
    },
    updateOwnerNoteError: (payload: ErrorResponse) => {
        return { type: actionTypes.UPDATE_OWNER_NOTE_ERROR, payload: payload };
    },
    updateTractNote: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        tractDetailData: TractDetailData
    ) => {
        return {
            type: actionTypes.UPDATE_TRACT_NOTE,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                tractDetailData,
            },
        };
    },
    updateTractNoteSuccess: () => {
        return { type: actionTypes.UPDATE_TRACT_NOTE_SUCCESS };
    },
    updateTractNoteError: (payload: ErrorResponse) => {
        return { type: actionTypes.UPDATE_TRACT_NOTE_ERROR, payload: payload };
    },
    saveExaminedHistory: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        examinedHistoryChanges: ExaminedHistoryChanges
    ) => {
        return {
            type: actionTypes.SAVE_HORIZON_EXAMINED_HISTORY,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                examinedHistoryChanges,
            },
        };
    },
    deleteOwnershipDetail: (payload: { projectId: number; ownershipDetailId: number }) => {
        return { type: actionTypes.DELETE_OWNERSHIP_DETAIL, payload };
    },
    saveAddOwnershipCollection: (clientNumber: number, projectNumber: number, addOwnershipCollection: AddOwnershipCollection) => {
        return {
            type: actionTypes.SAVE_ADD_OWNERSHIP_COLLECTION,
            payload: { clientNumber, projectNumber, addOwnershipCollection },
        };
    },
    createTract: (clientNumber: number, projectNumber: number, sectionNumber: number, newTract: NewTract) => {
        return {
            type: actionTypes.CREATE_TRACT,
            payload: { clientNumber, projectNumber, sectionNumber, newTract },
        };
    },
    deleteTract: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number) => {
        return {
            type: actionTypes.DELETE_TRACT,
            payload: { clientNumber, projectNumber, sectionNumber, tractNumber },
        };
    },
    deleteSection: (clientNumber: number, projectNumber: number, sectionNumber: number) => {
        return {
            type: actionTypes.DELETE_SECTION,
            payload: { clientNumber, projectNumber, sectionNumber },
        };
    },
    deleteSectionSuccess: (sectionNumber: number) => {
        return {
            type: actionTypes.DELETE_SECTION_SUCCESS,
            payload: { sectionNumber },
        };
    },
    deleteSectionError: (payload: ErrorResponse) => {
        return { type: actionTypes.DELETE_SECTION_ERROR, payload: payload };
    },
    deleteTownship: (clientNumber: number, projectNumber: number, townshipId: number) => {
        return {
            type: actionTypes.DELETE_TOWNSHIP,
            payload: { clientNumber, projectNumber, townshipId },
        };
    },
    deleteTownshipSuccess: (townshipId: number) => {
        return {
            type: actionTypes.DELETE_TOWNSHIP_SUCCESS,
            payload: { townshipId },
        };
    },
    deleteTownshipError: (payload: ErrorResponse) => {
        return { type: actionTypes.DELETE_TOWNSHIP_ERROR, payload: payload };
    },
    getSearchPagedOwners: (clientNumber: number, projectNumber: number, options: ODataOptions) => {
        return {
            type: actionTypes.GET_SEARCH_PAGED_OWNERS,
            payload: { clientNumber, projectNumber, options },
        };
    },
    getSearchPagedOwnersSuccess: (results: PagedResultSet<SearchResult>) => {
        return { type: actionTypes.GET_SEARCH_PAGED_OWNERS_SUCCESS, payload: results };
    },
    initOwnersPage: () => {
        return { type: actionTypes.INIT_OWNERS_PAGE };
    },
    getSearchOwners: (clientNumber: number, projectNumber: number, searchText: string, ownerType: string) => {
        return {
            type: actionTypes.GET_SEARCH_OWNERS,
            payload: { clientNumber, projectNumber, searchText, ownerType },
        };
    },
    getSearchOwnersSuccess: (results: Array<SearchResult>) => {
        return { type: actionTypes.GET_SEARCH_OWNERS_SUCCESS, payload: results };
    },
    getSearchOwnersDeviations: (clientNumber: number, projectNumber: number, searchText: string, ownerType: string) => {
        return {
            type: actionTypes.GET_SEARCH_OWNERS_DEVIATIONS,
            payload: { clientNumber, projectNumber, searchText, ownerType },
        };
    },
    getSearchOwnersDeviationsSuccess: (results: Array<SearchResult>) => {
        return {
            type: actionTypes.GET_SEARCH_OWNERS_DEVIATIONS_SUCCESS,
            payload: results,
        };
    },
    getFormations: (clientNumber: number, projectNumber: number) => {
        return {
            type: actionTypes.GET_FORMATIONS,
            payload: { clientNumber, projectNumber },
        };
    },
    getFormationsSuccess: (results: Array<Formation>) => {
        return { type: actionTypes.GET_FORMATIONS_SUCCESS, payload: results };
    },
    getPagedOwnerships: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        options: ODataOptions
    ) => {
        return {
            type: actionTypes.GET_PAGED_OWNERSHIPS,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                options,
            },
        };
    },
    createFormation: (clientNumber: number, projectNumber: number, formation: Formation) => {
        return {
            type: actionTypes.CREATE_FORMATION,
            payload: { clientNumber, projectNumber, formation },
        };
    },
    createFormationSuccess: () => {
        return { type: actionTypes.CREATE_FORMATION_SUCCESS };
    },
    getPagedOwnershipsSuccess: (results: PagedResultSet<Ownership>, horizonNumber: number) => {
        return {
            type: actionTypes.GET_PAGED_OWNERSHIPS_SUCCESS,
            payload: results,
            horizonNumber: horizonNumber,
        };
    },
    clearTractDetail: () => {
        return { type: actionTypes.CLEAR_TRACT_DETAIL };
    },
    initTractPage: () => {
        return { type: actionTypes.INIT_TRACT_PAGE };
    },
    sequenceHorizons: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        sequence: Array<{ id: number; sequence: number }>
    ) => {
        return {
            type: actionTypes.SEQUENCE_HORIZONS,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                sequence,
            },
        };
    },
    getUniqueHorizonIntervals: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number) => {
        return {
            type: actionTypes.GET_UNIQUE_HORIZONS,
            payload: { clientNumber, projectNumber, sectionNumber, tractNumber },
        };
    },
    getUniqueHorizonIntervalsSuccess: (horizonIntervals: Array<HorizonInterval>) => {
        return {
            type: actionTypes.GET_UNIQUE_HORIZONS_SUCCESS,
            payload: horizonIntervals,
        };
    },
    saveAddOwnership: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        values: AddOwnershipData
    ) => {
        return {
            type: actionTypes.SAVE_ADD_OWNERSHIP,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                values,
            },
        };
    },
    getTractOwners: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number) => {
        return {
            type: actionTypes.GET_TRACT_OWNERS,
            payload: { clientNumber, projectNumber, sectionNumber, tractNumber },
        };
    },
    getTractOwnersSuccess: (owners: Array<Owner>) => {
        return { type: actionTypes.GET_TRACT_OWNERS_SUCCESS, payload: owners };
    },
    getTownshipSectionsForSectionNumber: (clientNumber: number, projectNumber: number, sectionNumber: number) => {
        return {
            type: actionTypes.GET_TOWNSHIP_SECTIONS_FOR_SECTION,
            payload: { clientNumber, projectNumber, sectionNumber },
        };
    },
    deleteOwnership: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        ownershipId: number,
        ownershipOptions: ODataOptions
    ) => {
        return {
            type: actionTypes.DELETE_OWNERSHIP,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                ownershipId,
                ownershipOptions,
            },
        };
    },
    deleteHorizon: (clientNumber: number, projectNumber: number, sectionNumber: number, tractNumber: number, horizonNumber: number) => {
        return {
            type: actionTypes.DELETE_HORIZON,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
            },
        };
    },
    deleteHorizonSuccess: (horizonNumber: number) => {
        return { type: actionTypes.DELETE_HORIZON_SUCCESS, payload: horizonNumber };
    },
    deleteHorizonError: (payload: ErrorResponse) => {
        return { type: actionTypes.DELETE_HORIZON_ERROR, payload: payload };
    },
    promoteAdditionalOwnership: (
        clientNumber: number,
        projectNumber: number,
        sectionNumber: number,
        tractNumber: number,
        horizonNumber: number,
        ownershipId: number
    ) => {
        return {
            type: actionTypes.PROMOTE_ADDITIONAL_OWNERSHIP,
            payload: {
                clientNumber,
                projectNumber,
                sectionNumber,
                tractNumber,
                horizonNumber,
                ownershipId,
            },
        };
    },
    getTractsByOwner: (clientNumber: number, projectNumber: number, sectionNumber: number, ownerId: number) => {
        return {
            type: actionTypes.GET_TRACTS_BY_OWNER,
            payload: { clientNumber, projectNumber, sectionNumber, ownerId },
        };
    },
    getTractsByOwnerSuccess: (payload: Array<Tract>) => {
        return { type: actionTypes.GET_TRACTS_BY_OWNER_SUCCESS, payload: payload };
    },
    getSurfaceTractsByOwner: (clientNumber: number, projectNumber: number, fieldNumber: number, ownerId: number) => {
        return {
            type: actionTypes.GET_SURFACE_TRACTS_BY_OWNER,
            payload: { clientNumber, projectNumber, fieldNumber, ownerId },
        };
    },
    getSurfaceTractsByOwnerSuccess: (payload: Array<SurfaceTract>) => {
        return { type: actionTypes.GET_SURFACE_TRACTS_BY_OWNER_SUCCESS, payload: payload };
    },
    registerOwnerships: (ownerships: PagedResultSet<Ownership>, selectedHorizon: number) => {
        return { type: actionTypes.REGISTER_OWNERSHIPS, payload: { ownerships: ownerships, selectedHorizon: selectedHorizon } };
    }
};

// Reducer
const reducer: Reducer<ITractState> = (state: ITractState = initialState, action: Action): ITractState => {
    switch (action.type) {
        case actionTypes.CLEAR_SEARCH_RESULTS: {
            return {
                ...state,
                searchOwnersResults: undefined,
            };
        }
        case actionTypes.INIT_OWNER_LIST: {
            const payload = (action as { type: string; payload: ITableConfig }).payload;
            return {
                ...state,
                ownersPageConfig: !payload ? initialState.ownersPageConfig : payload,
            };
        }
        case actionTypes.INIT_TRACT_PAGE: {
            return {
                ...state,
                selectedSection: undefined,
                selectedTownship: undefined,
                tracts: Array<TractHorizonSummary>(),
                ownershipPageConfig: initialState.ownershipPageConfig,
            };
        }
        case actionTypes.INIT_OWNER_TRACTS: {
            return {
                ...state,
                ownerTracts: undefined,
                ownerSurfaceTracts: undefined,
            };
        }
        case actionTypes.GET_TRACT_DETAIL: {
            return {
                ...state,
                isTractLoading: true,
                tractDetail: undefined,
            };
        }
        case actionTypes.GET_TRACT_DETAIL_SUCCESS: {
            const payload = (action as { type: string; payload: TractDetailData }).payload;
            return {
                ...state,
                tractDetail: payload,
                isTractLoading: false,
            };
        }
        case actionTypes.INCREMENT_OWNERSHIP_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: PagedResultSet<Ownership>;
                }
            ).payload;

            let newOwnerships = payload;
            newOwnerships.items = state.ownerships.items.concat(payload.items);

            return {
                ...state,
                ownerships: newOwnerships,

                isTractLoading: false,
            };
        }
        case actionTypes.INCREMENT_OWNERS_PAGE_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: PagedResultSet<SearchResult>;
                }
            ).payload;

            const tempOwnersResults = _.cloneDeep(state.pagedSearchOwnersResults);

            if (payload.totalRecords > 0) {
                tempOwnersResults.items = [...tempOwnersResults.items].concat([...payload.items]);
            }

            return {
                ...state,
                pagedSearchOwnersResults: { ...tempOwnersResults },
                isTractLoading: false,
            };
        }
        case actionTypes.GET_CLONE_TRACT_DETAIL: {
            return {
                ...state,
                isTractLoading: true,
                cloneTract: undefined,
            };
        }
        case actionTypes.GET_CLONE_TRACT_DETAIL_SUCCESS: {
            const payload = (action as { type: string; payload: TractDetailData }).payload;
            return {
                ...state,
                cloneTract: payload,
                isTractLoading: false,
            };
        }
        case actionTypes.GET_CITY_STATE_SUCCESS: {
            const payload = (action as { type: string; payload: USPSCityState }).payload;
            return {
                ...state,
                uspsCityState: payload,
            };
        }
        case actionTypes.GET_OWNERSHIP_DETAIL: {
            return {
                ...state,
                isOwnershipDetailLoading: true,
            };
        }
        case actionTypes.GET_OWNERSHIP_DETAIL_SUCCESS: {
            const payload = (action as { type: string; payload: Ownership }).payload;
            return {
                ...state,
                ownershipDetail: payload,
                isOwnershipDetailLoading: false,
            };
        }
        case actionTypes.GET_TRACTS: {
            return {
                ...state,
                isTractsLoading: true,
            };
        }
        case actionTypes.GET_TRACTS_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: Array<TractHorizonSummary>;
                }
            ).payload;
            return {
                ...state,
                isTractsLoading: false,
                tracts: payload,
            };
        }
        case actionTypes.GET_CLONE_TRACTS: {
            return {
                ...state,
                isCloneTractLoading: true,
            };
        }
        case actionTypes.GET_CLONE_TRACTS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<TractDetail> }).payload;
            return {
                ...state,
                isCloneTractLoading: false,
                cloneTracts: payload,
            };
        }
        case actionTypes.GET_CLONE_SECTIONS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Section> }).payload;
            return {
                ...state,
                cloneTract: undefined,
                cloneSections: payload,
            };
        }
        case actionTypes.INCREMENT_OWNERSHIP_PAGE: {
            return {
                ...state,
                ownershipPageConfig: {
                    ...state.ownershipPageConfig,
                    pageNumber: state.ownershipPageConfig.pageNumber + 1,
                },
            };
        }
        case actionTypes.INCREMENT_OWNERS_PAGE: {
            return {
                ...state,
                ownersPageConfig: {
                    ...state.ownersPageConfig,
                    pageNumber: state.ownersPageConfig.pageNumber + 1
                }
            };
        }
        case actionTypes.GET_CLONE_TOWNSHIPS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Township> }).payload;
            return {
                ...state,
                cloneSections: undefined,
                cloneTracts: undefined,
                cloneTownships: payload,
            };
        }
        case actionTypes.GET_TOWNSHIPS: {
            return {
                ...state,
                isTownshipsLoading: true,
            };
        }
        case actionTypes.GET_TOWNSHIP: {
            return {
                ...state,
                isTownshipLoading: true,
            };
        }
        case actionTypes.GET_EASTERN_TOWNSHIP: {
            return {
                ...state,
                isTownshipLoading: true,
            };
        }
        case actionTypes.GET_SECTIONS: {
            return {
                ...state,
                isSectionsLoading: true,
            };
        }
        case actionTypes.GET_MERIDIANS: {
            return {
                ...state,
                isMeridiansLoading: true,
            };
        }
        case actionTypes.GET_TOWNSHIPS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Township> }).payload;
            const first = payload.sort((t: Township, t2: Township) => t.townshipNumber - t2.townshipNumber)[0];
            return {
                ...state,
                selectedTownship: state.selectedTownship ? state.selectedTownship : first,
                isTownshipsLoading: false,
                townships: payload,
            };
        }
        case actionTypes.GET_TOWNSHIP_SUCCESS: {
            const payload = (action as { type: string; payload: Township }).payload;
            return {
                ...state,
                isTownshipLoading: false,
                township: payload,
            };
        }
        case actionTypes.GET_EASTERN_TOWNSHIP_SUCCESS: {
            const payload = (action as { type: string; payload: EasternTownship }).payload;
            return {
                ...state,
                isTownshipLoading: false,
                easternTownship: payload,
            };
        }
        case actionTypes.GET_EASTERN_TOWNSHIPS_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: Array<EasternTownship>;
                }
            ).payload;
            return {
                ...state,
                isTownshipsLoading: false,
                easternMaps: [],
                easternTownships: payload,
            };
        }
        case actionTypes.GET_EASTERN_MAP_SUCCESS: {

            const { payload } = action as { type: string; payload: EasternMap };

            const maps = [...state.easternMaps];

            const idx = _.findIndex(state.easternMaps, {
                dlcNumber: payload.dlcNumber,
            });

            if (idx >= 0) {
                maps[idx] = payload;
            } else {
                maps.push(payload);
            }

            return {
                ...state,
                easternMaps: [...maps],
                easternMap: payload,
            };
        }
        case actionTypes.GET_SECTIONS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Section> }).payload;
            return {
                ...state,
                isSectionsLoading: false,
                sections: payload,
            };
        }
        case actionTypes.GET_TOWNSHIP_SECTIONS_FOR_SECTION_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: { sections: Array<Section>; townships: Array<Township> };
                }
            ).payload;
            return {
                ...state,
                sections: payload.sections,
                townships: payload.townships,
                selectedTownship: _.find(payload.townships, {
                    id: payload.sections[0].townshipId,
                }),
                selectedSection: _.find(payload.sections, {
                    dlcNumber: state.selectedSection.dlcNumber,
                }),
                isSectionsLoading: false,
            };
        }
        case actionTypes.GET_MERIDIANS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Meridian> }).payload;
            return {
                ...state,
                isMeridiansLoading: false,
                meridians: payload,
            };
        }
        case actionTypes.SELECT_TOWNSHIP: {
            const payload = (
                action as {
                    type: string;
                    payload: { projectId: number; township: Township };
                }
            ).payload;
            return {
                ...state,
                selectedTownship: payload.township,
            };
        }
        case actionTypes.SELECT_SECTION: {
            const payload = (
                action as {
                    type: string;
                    payload: {
                        clientNumber: number;
                        projectNumber: number;
                        sectionNumber: number;
                    };
                }
            ).payload;
            const sec = new Section();
            sec.dlcNumber = payload.sectionNumber;
            return {
                ...state,
                selectedSection: sec,
            };
        }

        case actionTypes.CLEAR_ERROR: {
            return {
                ...state,
                error: undefined,
            };
        }
        case actionTypes.CREATE_TOWNSHIP_SUCCESS: {
            const payload = (action as { type: string; payload: Township }).payload;
            return {
                ...state,
                selectedTownship: payload,
                selectedSection: undefined,
                sections: undefined,
                error: undefined,
            };
        }

        case actionTypes.CREATE_TOWNSHIP_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
            };
        }

        case actionTypes.CREATE_SECTION_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: { townshipId: number; section: Section };
                }
            ).payload;
            const township = state.townships.filter((twn: Township) => twn.id === payload.townshipId);
            if (township && township.length > 0) {
                township[0].sectionCount += 1;
            }
            return {
                ...state,
                selectedSection: payload.section,
                townships: [...state.townships],
            };
        }
        case actionTypes.UPDATE_SECTION_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: { townshipId: number; section: Section };
                }
            ).payload;
            return {
                ...state,
                selectedSection: payload.section,
                townships: [...state.townships],
            };
        }
        case actionTypes.SAVE_TRACT_DETAIL: {
            return {
                ...state,
                ownershipPageConfig: initialState.ownershipPageConfig,
                isTractLoading: true,
            };
        }
        case actionTypes.SAVE_OWNERSHIP_DETAIL: {
            return {
                ...state,
                isTractLoading: true,
            };
        }
        case actionTypes.GET_SEARCH_PAGED_OWNERS: {
            return {
                ...state,
                isLoadingSearchOwners: true,
                ownersPageConfig: initialState.ownersPageConfig
            };
        }
        case actionTypes.GET_SEARCH_PAGED_OWNERS_SUCCESS: {
            const payload = (action as { type: string; payload: PagedResultSet<SearchResult> }).payload;
            return {
                ...state,
                isLoadingSearchOwners: false,
                pagedSearchOwnersResults: payload
            };
        }
        case actionTypes.INIT_OWNERS_PAGE: {
            return {
                ...state,
                pagedSearchOwnersResults: undefined,
                ownersPageConfig: initialState.ownersPageConfig          
            };
        }
        case actionTypes.GET_SEARCH_OWNERS: {
            return {
                ...state,
                isLoadingSearchOwners: true,
            };
        }
        case actionTypes.GET_SEARCH_OWNERS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<SearchResult> }).payload;
            return {
                ...state,
                isLoadingSearchOwners: false,
                searchOwnersResults: payload,
            };
        }
        case actionTypes.GET_SEARCH_OWNERS_DEVIATIONS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<SearchResult> }).payload;
            return {
                ...state,
                isLoadingSearchOwners: false,
                searchOwnersDeviationsResults: payload,
            };
        }
        case actionTypes.CLEAR_SEARCH_OWNERS_RESULTS: {
            return {
                ...state,
                searchOwnersResults: undefined,
            };
        }
        case actionTypes.CLEAR_TRACT_DETAIL: {
            return {
                ...state,
                tractDetail: undefined,
            };
        }
        case actionTypes.GET_FORMATIONS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Formation> }).payload;
            return {
                ...state,
                formations: payload,
            };
        }
        case actionTypes.GET_PAGED_OWNERSHIPS_SUCCESS: {
            const newData = action as {
                type: string;
                payload: PagedResultSet<Ownership>;
                horizonNumber: number;
            };

            return {
                ...state,
                ownerships: newData.payload,
                ownershipPageConfig: initialState.ownershipPageConfig,
            };
        }
        case actionTypes.GET_UNIQUE_HORIZONS_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: Array<HorizonInterval>;
                }
            ).payload;

            return {
                ...state,
                uniqueHorizonIntervals: payload,
            };
        }
        case actionTypes.GET_TRACT_OWNERS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Owner> }).payload;

            return {
                ...state,
                tractDetail: {
                    ...state.tractDetail,
                    owners: payload,
                },
            };
        }
        case actionTypes.DELETE_TRACT: {
            const payload = (
                action as {
                    type: string;
                    payload: { tractNumber: number };
                }
            ).payload;
            return {
                ...state,
                tracts: state.tracts.filter((x) => x.tractNumber !== payload.tractNumber),
            };
        }
        case actionTypes.DELETE_SECTION_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: { sectionNumber: number };
                }
            ).payload;
            return {
                ...state,
                sections: state.sections.filter((x) => x.dlcNumber !== payload.sectionNumber),
            };
        }
        case actionTypes.DELETE_SECTION_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
            };
        }
        case actionTypes.DELETE_TOWNSHIP_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: { townshipId: number };
                }
            ).payload;
            return {
                ...state,
                townships: state.townships.filter((x) => x.id !== payload.townshipId),
            };
        }
        case actionTypes.DELETE_TOWNSHIP_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
            };
        }
        case actionTypes.UPDATE_TRACT_NOTE_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
            };
        }
        // case actionTypes.UPDATE_OWNER_NOTE_SUCCESS: {
        //     const payload = (action as { type: string, payload: Ownership }).payload;
        //     let tempTractDetail = _.cloneDeep(state.tractDetail);
        //     tempTractDetail?.horizons.forEach(x => {
        //         x.ownership?.items.forEach(
        //             y => {
        //                 if (y.ownerLookUpId === payload.ownerLookUpId) {
        //                     y.note = payload.note;
        //                 }
        //             });
        //     }
        //     );
        //     return {
        //         ...state,
        //         tractDetail: tempTractDetail
        //     };
        // }
        case actionTypes.UPDATE_OWNER_NOTE_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
            };
        }
        case actionTypes.DELETE_HORIZON_SUCCESS: {
            const payload = (action as { type: string; payload: number }).payload;

            const tempTractDetail = _.cloneDeep(state.tractDetail);
            tempTractDetail.horizons = tempTractDetail?.horizons.filter((x) => x.horizonInterval.horizonNumber !== payload);
            return {
                ...state,
                tractDetail: tempTractDetail,
            };
        }
        case actionTypes.DELETE_HORIZON_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
            };
        }
        case actionTypes.GET_TRACTS_BY_OWNER_SUCCESS: {
            const payload = (action as { type: string; payload: Array<Tract> }).payload;
            return {
                ...state,
                ownerTracts: payload,
            };
        }
        case actionTypes.GET_SURFACE_TRACTS_BY_OWNER_SUCCESS: {
            const payload = (action as { type: string; payload: Array<SurfaceTract> }).payload;
            return {
                ...state,
                ownerSurfaceTracts: payload
            };
        }
        case actionTypes.REGISTER_OWNERSHIPS: {
            const payload = (action as { type: string; payload: { ownerships: PagedResultSet<Ownership>, selectedHorizon: number }}).payload;
            const tempTractDetail = _.cloneDeep(state.tractDetail);
            let horizon = tempTractDetail.horizons.find(h => h.horizonInterval.horizonNumber === payload.selectedHorizon);
            tempTractDetail.horizons[tempTractDetail.horizons.indexOf(horizon)].ownership = payload.ownerships;
            return {
                ...state,
                tractDetail: tempTractDetail
            };
        }
        default:
            return state;
    }
};

export default reducer;

// Selectors
const tractDetailSelector = (state: IRootState): TractDetailData | undefined => state.tract.tractDetail;
const tractsSelector = (state: IRootState): Array<TractHorizonSummary> => (state.tract.tracts ? state.tract.tracts : undefined);
const townshipsSelector = (state: IRootState): Array<Township> =>
    state.tract && state.tract.townships
        ? state.tract.townships.sort((t: Township, t2: Township) => t.townshipNumber - t2.townshipNumber)
        : undefined;
const sectionsSelector = (state: IRootState): Array<Section> =>
    state.tract && state.tract.sections ? state.tract.sections.sort((s: Section, s2: Section) => s.sectionNumber - s2.sectionNumber) : undefined;
const meridiansSelector = (state: IRootState): Array<Meridian> => state.tract.meridians;
const selectedTownship = (state: IRootState): Township => state.tract.selectedTownship;
const ownershipDetailSelector = (state: IRootState): Ownership => state.tract.ownershipDetail;
const ownershipsSelector = (state: IRootState): PagedResultSet<Ownership> => state.tract.ownerships;
const searchOwnersResultsSelector = (state: IRootState): Array<SearchResult> => state.tract.searchOwnersResults;
const searchPagedOwnersResultsSelector = (state: IRootState): PagedResultSet<SearchResult> => state.tract.pagedSearchOwnersResults;
const searchOwnersDeviationsResultsSelector = (state: IRootState): Array<SearchResult> => state.tract.searchOwnersDeviationsResults;
const formationsSelector = (state: IRootState): Array<Formation> => state.tract.formations;
const uniqueHorizonIntervalsSelector = (state: IRootState): Array<HorizonInterval> => state.tract.uniqueHorizonIntervals;
const isSectionsLoadingSelector = (state: IRootState): boolean => state.tract.isSectionsLoading;
const isTractsLoadingSelector = (state: IRootState): boolean => state.tract.isTractsLoading;
const easternMapSelector = (state: IRootState, mapNumber: number): EasternMap => _.find(state.tract.easternMaps, { dlcNumber: mapNumber });

export const selectors = {
    tractDetail: tractDetailSelector,
    tracts: tractsSelector,
    townships: townshipsSelector,
    sections: sectionsSelector,
    meridians: meridiansSelector,
    selectedTownship,
    selectedSection: (state: IRootState): Section => state.tract.selectedSection,
    ownershipDetailSelector: ownershipDetailSelector,
    ownershipsSelector: ownershipsSelector,
    searchOwnersResults: searchOwnersResultsSelector,
    searchPagedOwnersResultsSelector: searchPagedOwnersResultsSelector,
    searchOwnersDeviationsResults: searchOwnersDeviationsResultsSelector,
    formations: formationsSelector,
    uniqueHorizonIntervals: uniqueHorizonIntervalsSelector,
    isSectionsLoading: isSectionsLoadingSelector,
    isTractsLoading: isTractsLoadingSelector,
    error: (state: IRootState): string => state.tract.error,
    ownershipPageConfig: (state: IRootState): ITableConfig => state.tract.ownershipPageConfig,
    ownersPageConfig: (state: IRootState): ITableConfig => state.tract.ownersPageConfig,
    easternMap: easternMapSelector,
};

// Side-Effects
const sagas = {
    // tslint:disable-next-line:max-line-length
    getTractDetail: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            options: ODataOptions;
        };
    }): unknown {
        if (action.payload.tractNumber !== undefined) {
            yield call(layoutActionCreators.waitIniitalLoadState);
            const jwt = yield select(authSelectors.jwt);
            const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options } = action.payload;
            const tableConfig = yield select(selectors.ownershipPageConfig);
            const dataOptions = options ? options : ODataOptions.from(tableConfig);

            const [result] = yield all([
                call(Api.Tract.getTractDetail, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, dataOptions, jwt),
                delay(200),
            ]);
            yield put(actionCreators.getTractDetailSuccess(result));
        }
    },
    incrementOwnership: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
        };
    }): unknown {
        if (action.payload.tractNumber !== undefined) {
            yield call(layoutActionCreators.waitIniitalLoadState);
            const jwt = yield select(authSelectors.jwt);
            const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber } = action.payload;

            const tableConfig = yield select(selectors.ownershipPageConfig);
            const options = ODataOptions.from(tableConfig);
            const [result] = yield all([
                call(Api.Tract.getPagedOwnerships, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options, jwt),
                delay(200),
            ]);
            yield put(actionCreators.incrementOwnershipSuccess(result));
        }
    },
    incrementOwners: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            searchText: string;
            options: ODataOptions;
        }
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber } = action.payload;

        const tableConfig = yield select(selectors.ownersPageConfig);
        const options = ODataOptions.from(tableConfig);

        if (action.payload) {
            options.merge(action.payload.options);
        }

        const [result] = yield all([
            call(Api.Tract.getSearchPagedOwners, clientNumber, projectNumber, options, jwt),
            delay(200)
        ]);
        yield put(actionCreators.incrementOwnersSuccess(result));
    },
    // tslint:disable-next-line:max-line-length
    getCloneTractDetail: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            options: ODataOptions;
        };
    }): unknown {
        if (action.payload.tractNumber !== undefined) {
            yield call(layoutActionCreators.waitIniitalLoadState);
            const jwt = yield select(authSelectors.jwt);
            const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options } = action.payload;
            const [result] = yield all([
                call(Api.Tract.getTractDetail, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options, jwt),
                delay(200),
            ]);
            yield put(actionCreators.getCloneTractDetailSuccess(result));
        }
    },
    getOwnershipDetail: function* (action: {
        type: string;
        payload: {
            ownerId: number;
            horizonId: number;
            projectId: number;
            tractId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(
                Api.Tract.getOwnershipDetail,
                action.payload.ownerId,
                action.payload.horizonId,
                action.payload.projectId,
                action.payload.tractId,
                jwt
            ),
            delay(200),
        ]);
        yield put(actionCreators.getOwnershipDetailSuccess(result));
    },
    getTractsByOwner: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            ownerId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, ownerId } = action.payload;
        const [result] = yield all([call(Api.Tract.getTractsByOwner, clientNumber, projectNumber, sectionNumber, ownerId, jwt), delay(200)]);
        yield put(actionCreators.getTractsByOwnerSuccess(result));
    },
    getSurfaceTractsByOwner: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            fieldNumber: number;
            ownerId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, fieldNumber, ownerId } = action.payload;
        const [result] = yield all([call(Api.Tract.getSurfaceTractsByOwner, clientNumber, projectNumber, fieldNumber, ownerId, jwt), delay(200)]);
        yield put(actionCreators.getSurfaceTractsByOwnerSuccess(result));
    },
    getTracts: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber } = action.payload;
        const [result] = yield all([call(Api.Tract.getTracts, clientNumber, projectNumber, sectionNumber, jwt), delay(200)]);
        yield put(actionCreators.getTractsSuccess(result));
    },
    getCloneTracts: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(Api.Tract.getCloneTracts, action.payload.clientNumber, action.payload.projectNumber, action.payload.sectionNumber, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getCloneTractsSuccess(result));
    },
    getCloneSections: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(Api.Tract.getSectionsByTownship, action.payload.clientNumber, action.payload.projectNumber, action.payload.townshipId, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getCloneSectionsSuccess(result));
    },
    getCloneTownships: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Tract.getTownships, action.payload.clientNumber, action.payload.projectNumber, jwt), delay(200)]);
        yield put(actionCreators.getCloneTownshipsSuccess(result));
    },
    selectTownship: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            township: Township;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        if (action.payload.township !== undefined) {
            const { clientNumber, projectNumber, township } = action.payload;
            yield put(actionCreators.getSections(clientNumber, projectNumber, township.id));
        }
    },
    selectSection: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        if (action.payload.sectionNumber !== undefined) {
            const { clientNumber, projectNumber, sectionNumber } = action.payload;
            yield put(actionCreators.getTracts(clientNumber, projectNumber, sectionNumber, ''));
        }
    },
    getSections: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(Api.Tract.getSectionsByTownship, action.payload.clientNumber, action.payload.projectNumber, action.payload.townshipId, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getSectionsSuccess(result));
    },
    getMap: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number; mapNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(Api.Tract.getEasternMap, action.payload.clientNumber, action.payload.projectNumber, action.payload.mapNumber, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getMapSuccess(result));
    },
    getTownships: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Tract.getTownships, action.payload.clientNumber, action.payload.projectNumber, jwt), delay(200)]);
        const first = result && result.sort((t: Township, t2: Township) => t.townshipNumber - t2.townshipNumber)[0];
        if (first) {
            yield put(actionCreators.getSections(action.payload.clientNumber, action.payload.clientNumber, first.id));
        }
        yield put(actionCreators.getTownshipsSuccess(result));
    },
    getEasternTownships: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Tract.getEasternTownships, action.payload.clientNumber, action.payload.projectNumber, jwt), delay(200)]);
        if (!result.isError) {
            yield put(actionCreators.getEasternTownshipsSuccess(result));
        }
    },
    getTownship: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number, townshipId: number }}): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Tract.getTownship, action.payload.clientNumber, action.payload.projectNumber, action.payload.townshipId, jwt), delay(200)]);
        if (!result.isError) {
            yield put(actionCreators.getTownshipSuccess(result));
        }
    },
    getEasternTownship: function* (action: { type: string; payload: { clientNumber: number, projectNumber: number, townshipId: number }}): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Tract.getEasternTownship, action.payload.clientNumber, action.payload.projectNumber, action.payload.townshipId, jwt), delay(200)]);
        if (!result.isError) {
            yield put(actionCreators.getEasternTownshipSuccess(result));
        }
    },
    getMeridians: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(Api.Tract.getMeridiansByProject, action.payload.clientNumber, action.payload.projectNumber, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getMeridiansSuccess(result));
    },
    createTownship: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            township: Township;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, township } = action.payload;
        const result = yield call(Api.Tract.createTownship, clientNumber, projectNumber, township, jwt);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.createTownshipFailure(result));
        } else {
            action.payload.township.id = result.id;
            yield put(actionCreators.createTownshipSuccess(action.payload.township));
            yield put(actionCreators.getTownships(clientNumber, projectNumber));
            yield put(tagActionCreators.getProjectTags(clientNumber, projectNumber));
            yield put(layoutActionCreators.hideDialog());
        }
    },
    createEasternTownship: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            township: EasternTownship;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, township } = action.payload;
        const result = yield call(Api.Tract.createEasternTownship, clientNumber, projectNumber, township, jwt);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.createTownshipFailure(result));
        } else {
            action.payload.township.id = result.id;
            yield put(actionCreators.createEasternTownshipSuccess(action.payload.township));
            yield put(actionCreators.getTownships(clientNumber, projectNumber));
            yield put(tagActionCreators.getProjectTags(clientNumber, projectNumber));
            yield put(layoutActionCreators.hideDialog());
        }
    },
    createEasternMap: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipId: number;
            map: EasternMap;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, townshipId, map } = action.payload;
        const result = yield call(Api.Tract.createEasternMap, clientNumber, projectNumber, townshipId, map, jwt);
        action.payload.map.id = result;
        yield put(actionCreators.createEasternMapSuccess(action.payload.map));
        yield put(layoutActionCreators.hideDialog());
    },
    updateTownship: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            township: Township;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, township } = action.payload;
        const result = yield call(Api.Tract.updateTownship, clientNumber, projectNumber, township, jwt);
        action.payload.township.id = result.id;
        yield put(actionCreators.updateTownshipSuccess(action.payload.township));
        yield put(actionCreators.getTownships(clientNumber, projectNumber));
        yield put(tagActionCreators.getProjectTags(clientNumber, projectNumber));
        yield put(layoutActionCreators.hideDialog());
    },
    createSection: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipId: number;
            section: Section;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, townshipId, section } = action.payload;
        yield call(Api.Tract.createSection, clientNumber, projectNumber, townshipId, section, jwt);
        yield put(actionCreators.getSections(clientNumber, projectNumber, townshipId));
        yield put(actionCreators.getTownships(clientNumber, projectNumber));
        yield put(tagActionCreators.getProjectTags(clientNumber, projectNumber));
        yield put(layoutActionCreators.hideDialog());
    },
    updateSection: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipId: number;
            section: Section;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, townshipId, section } = action.payload;
        yield call(Api.Tract.updateSection, clientNumber, projectNumber, townshipId, section, jwt);
        yield put(actionCreators.getSections(clientNumber, projectNumber, townshipId));
        yield put(tagActionCreators.getProjectTags(clientNumber, projectNumber));
        yield put(layoutActionCreators.hideDialog());
    },
    // tslint:disable-next-line:max-line-length
    saveTractDetail: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            formValues: TractDetailFormData;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, formValues } = action.payload;
        // TODO: update to use horizonNumber on saveTractDetail API
        yield all([call(Api.Tract.saveTractDetail, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, formValues, jwt), delay(200)]);

        yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));
    },
    saveExaminedHistory: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            examinedHistoryChanges: ExaminedHistoryChanges;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, examinedHistoryChanges } = action.payload;
        // TODO: redo saveExaminedHistory with horizonNumber and resolve rather than peeling horizonId off examinedHistoryChanges
        yield all([
            call(Api.Tract.saveExaminedHistory, clientNumber, projectNumber, sectionNumber, tractNumber, examinedHistoryChanges, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));
    },
    deleteOwnershipDetail: function* (action: { type: string; payload: { projectId: number; ownershipDetailId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield all([call(Api.Tract.deleteOwnershipDetail, action.payload.projectId, action.payload.ownershipDetailId, jwt), delay(200)]);
    },
    saveAddOwnershipCollection: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            addOwnershipCollection: AddOwnershipCollection;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, addOwnershipCollection } = action.payload;
        yield all([
            call(Api.Tract.saveAddOwnershipCollection, clientNumber, projectNumber, sectionNumber, tractNumber, addOwnershipCollection, jwt),
            delay(200),
        ]);
    },
    createTract: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            newTract: NewTract;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([
            call(
                Api.Tract.createTract,
                action.payload.clientNumber,
                action.payload.projectNumber,
                action.payload.sectionNumber,
                action.payload.newTract,
                jwt
            ),
            delay(200),
        ]);
        // tslint:disable-next-line
        yield put(
            replace(
                `/clients/${action.payload.clientNumber}/projects/${action.payload.projectNumber}/sections/${action.payload.sectionNumber}/tracts/${result}/horizons/1`
            )
        );
    },
    deleteTract: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield all([
            call(
                Api.Tract.deleteTract,
                action.payload.clientNumber,
                action.payload.projectNumber,
                action.payload.sectionNumber,
                action.payload.tractNumber,
                jwt
            ),
            delay(200),
        ]);
        // tslint:disable-next-line
        // const clientId = yield select((state: IRootState) => state.router.location.search.match["clientId"]);
        // yield put(replace(`/clients/${action.payload.clientNumber}/projects/${action.payload.projectNumber}/sections/${action.payload.sectionNumber}/tracts/${result}`));
    },
    deleteSection: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber } = action.payload;
        const [result] = yield all([call(Api.Tract.deleteSection, clientNumber, projectNumber, sectionNumber, jwt), delay(200)]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.deleteSectionError(result));
        } else {
            yield put(actionCreators.deleteSectionSuccess(sectionNumber));
            yield put(layoutActionCreators.hideDialog());
            yield put(actionCreators.getTownships(clientNumber, projectNumber));
        }
    },
    deleteTownship: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, townshipId } = action.payload;
        const [result] = yield all([call(Api.Tract.deleteTownship, clientNumber, projectNumber, townshipId, jwt), delay(200)]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.deleteTownshipError(result));
        } else {
            yield put(actionCreators.deleteTownshipSuccess(townshipId));
            yield put(layoutActionCreators.hideDialog());
        }
    },
    getSearchPagedOwners: function* (action: {
        type: string;
        payload: {
            clientNumber: number,
            projectNumber: number,
            options: ODataOptions
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.ownersPageConfig);
        const options = ODataOptions.from(tableConfig);

        if (action.payload) {
            options.merge(action.payload.options);
        }

        const { clientNumber, projectNumber } = action.payload;
        const result = yield call(Api.Tract.getSearchPagedOwners, clientNumber, projectNumber, options, jwt);
        yield put(actionCreators.getSearchPagedOwnersSuccess(result));
    },
    getSearchOwners: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            searchText: string;
            ownerType: string;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, searchText, ownerType } = action.payload;
        const result = yield call(Api.Tract.getSearchOwners, clientNumber, projectNumber, searchText, ownerType, jwt);
        yield put(actionCreators.getSearchOwnersSuccess(result));
    },
    getSearchDeviationsOwners: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            searchText: string;
            ownerType: string;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, searchText, ownerType } = action.payload;
        const result = yield call(Api.Tract.getSearchOwners, clientNumber, projectNumber, searchText, ownerType, jwt);
        yield put(actionCreators.getSearchOwnersDeviationsSuccess(result));
    },
    getFormations: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number } }): unknown {
        const result = yield apiCall(action.type, Api.Tract.getFormations, action.payload.clientNumber, action.payload.projectNumber);
        yield put(actionCreators.getFormationsSuccess(result));
    },
    createFormation: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            formation: Formation;
        };
    }): unknown {
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, formation } = action.payload;
        yield call(Api.Tract.createFormation, clientNumber, projectNumber, formation, jwt);
        yield put(actionCreators.getFormations(clientNumber, projectNumber));
    },
    // tslint:disable-next-line:max-line-length
    getPagedOwnerships: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            options: ODataOptions;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        // tslint:disable-next-line:max-line-length
        const result = yield call(
            Api.Tract.getPagedOwnerships,
            action.payload.clientNumber,
            action.payload.projectNumber,
            action.payload.sectionNumber,
            action.payload.tractNumber,
            action.payload.horizonNumber,
            action.payload.options,
            jwt
        );
        yield put(actionCreators.getPagedOwnershipsSuccess(result, action.payload.horizonNumber));
    },
    // tslint:disable-next-line:max-line-length
    saveAddOwnership: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            values: AddOwnershipData;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, values } = action.payload;
        yield call(Api.Tract.saveAddOwnership, clientNumber, projectNumber, sectionNumber, tractNumber, values, jwt);

        const tableConfig = yield select(selectors.ownershipPageConfig);
        const options = ODataOptions.from(tableConfig);

        yield put(actionCreators.getTractOwners(clientNumber, projectNumber, sectionNumber, tractNumber));
        const tractDetail: TractDetailData = yield select(selectors.tractDetail);
        const horizon = tractDetail.horizons.find((x) => x.horizonInterval.horizonNumber === horizonNumber);
        if (horizon.ownership.totalRecords === 0) {
            yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options));
        } else if (horizon.ownership.totalRecords % horizon.ownership.pageSize === 0) {
            yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options));
        } else {
            // const option2 = new ODataOptions().addSkipTop(0, options.getPageSize() * options.getPageNumber());
            yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options));
        }
    },
    createHorizon: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizon: CreateHorizonDto;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizon } = action.payload;
        const horizonNumber = yield call(Api.Tract.createHorizon, clientNumber, projectNumber, sectionNumber, tractNumber, horizon, jwt);
        // TODO: navigate to URL instead of calling getTractDetail
        // yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));

        if (!isNaN(horizonNumber)) {
            yield put(replace(`/`));
            yield put(
                replace(`/clients/${clientNumber}/projects/${projectNumber}/sections/${sectionNumber}/tracts/${tractNumber}/horizons/${horizonNumber}`)
            );
   
            yield put(layoutActionCreators.hideDialog());
            yield put(actionCreators.createHorizonSuccess(horizonNumber));
        }
    },
    updateHorizonInterval: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            horizon: HorizonInterval;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, horizon } = action.payload;
        yield call(Api.Tract.updateHorizonInterval, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, horizon, jwt);
        yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));
    },
    updateTractNote: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            tractDetailData: TractDetailData;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, tractDetailData } = action.payload;

        const [result] = yield all([
            yield call(Api.Tract.updateTractNote, clientNumber, projectNumber, sectionNumber, tractNumber, tractDetailData, jwt),
            delay(200),
        ]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.updateTractNoteError(result));
        } else {
            yield put(actionCreators.updateTractNoteSuccess());
            yield put(layoutActionCreators.hideDialog());
            yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));
        }
    },
    updateOwnerNote: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            ownership: Ownership;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, ownership } = action.payload;

        // TODO: updateOwnerNote, change backend to use horizonId after resolving horizonNumber
        const [result] = yield all([
            yield call(Api.Tract.updateOwnerNote, clientNumber, projectNumber, sectionNumber, tractNumber, ownership, jwt),
            delay(200),
        ]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.updateOwnerNoteError(result));
        } else {
            yield put(actionCreators.updateOwnerNoteSuccess());
            // const tractDetail: TractDetailData = yield select(selectors.tractDetail);
            // let horizon = tractDetail.horizons.find(x => x.horizonInterval.horizonNumber === horizonNumber);
            // const tableConfig = yield select(selectors.ownershipPageConfig);
            // const options = ODataOptions.from(tableConfig);
            // if (horizon.ownership.totalRecords === 0) {
            //     yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options));
            // } else if (horizon.ownership.totalRecords % horizon.ownership.pageSize === 0) {
            //     yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, options));
            // } else {

            //     let option2 = new ODataOptions().addSkipTop(0, options.getPageSize() * options.getPageNumber());
            //     yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, option2));
            // }
            yield put(layoutActionCreators.hideDialog());
            location.reload();
        }
    },
    sequenceHorizons: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            sequence: Array<{ id: number; sequence: number }>;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, sequence } = action.payload;
        yield call(Api.Tract.sequenceHorizons, clientNumber, projectNumber, sectionNumber, tractNumber, sequence, jwt);
        yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));
        yield put(layoutActionCreators.hideDialog());
    },
    getUniqueHorizonIntervals: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber } = action.payload;
        const result = yield call(Api.Tract.getUniqueHorizonIntervals, clientNumber, projectNumber, sectionNumber, tractNumber, jwt);
        yield put(actionCreators.getUniqueHorizonIntervalsSuccess(result));
    },
    getTractOwners: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const result = yield call(
            Api.Tract.getTractOwners,
            action.payload.clientNumber,
            action.payload.projectNumber,
            action.payload.sectionNumber,
            action.payload.tractNumber,
            jwt
        );
        yield put(actionCreators.getTractOwnersSuccess(result));
    },
    getTownshipSectionsForSectionNumber: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber } = action.payload;
        const [result1] = yield all([
            call(Api.Tract.getTownshipSectionsForSectionNumber, clientNumber, projectNumber, sectionNumber, jwt),
            delay(200),
        ]);
        // yield put(actionCreators.editClientSuccess(result));
        const [result2] = yield all([call(Api.Tract.getTownships, clientNumber, projectNumber, jwt)]);
        yield put(
            actionCreators.getTownshipSectionsBySectionsSuccess({
                sections: result1,
                townships: result2,
            })
        );
    },
    // tslint:disable-next-line:max-line-length
    deleteOwnership: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            ownershipId: number;
            ownershipOptions: ODataOptions;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, ownershipId, ownershipOptions } = action.payload;

        yield call(Api.Tract.deleteOwnership, clientNumber, projectNumber, sectionNumber, ownershipId, jwt);
        const tableConfig = yield select(selectors.ownershipPageConfig);
        const options = ODataOptions.from(tableConfig);
        options.addSkipTop(0, options.getPageSize() * options.getPageNumber());

        // yield put(actionCreators.getPagedOwnerships(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, ownershipOptions));
        yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, ownershipOptions));
    },
    deleteHorizon: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber } = action.payload;

        const [result] = yield all([
            call(Api.Tract.deleteHorizon, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, jwt),
            delay(200),
        ]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.deleteHorizonError(result));
        } else {
            const tractDetail: TractDetailData = yield select(selectors.tractDetail);
            const destinationHorizon = tractDetail.horizons.filter((x) => x.horizonInterval.horizonNumber !== horizonNumber)[0].horizonInterval
                .horizonNumber;
            yield put(layoutActionCreators.hideDialog());
            // tslint:disable-next-line:max-line-length
            yield put(
                replace(
                    `/clients/${action.payload.clientNumber}/projects/${action.payload.projectNumber}/sections/${action.payload.sectionNumber}/tracts/${action.payload.tractNumber}/horizons/${destinationHorizon}`
                )
            );
            yield put(actionCreators.deleteHorizonSuccess(horizonNumber));
        }
    },
    promoteAdditionalOwnership: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            sectionNumber: number;
            tractNumber: number;
            horizonNumber: number;
            ownershipId: number;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, ownershipId } = action.payload;

        yield all([
            call(Api.Tract.promoteAdditionalOwnership, clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, ownershipId, jwt),
            delay(200),
        ]);

        yield put(actionCreators.getTractDetail(clientNumber, projectNumber, sectionNumber, tractNumber, horizonNumber, undefined));
    },

    getCityState: function* (action: { type: string; payload: { zipCode: string } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { zipCode } = action.payload;

        const [result] = yield all([call(Api.Tract.getCityState, zipCode, jwt), delay(200)]);

        if (result instanceof ErrorResponse) {
            yield put(actionCreators.getCityStateError(result));
        } else {
            yield put(actionCreators.getCityStateSuccess(result.zipCode));
        }
    },
};

const watches = {
    getCityState: function* () {
        yield takeEvery(actionTypes.GET_CITY_STATE, sagas.getCityState);
    },
    getTractDetail: function* () {
        yield takeEvery(actionTypes.GET_TRACT_DETAIL, sagas.getTractDetail);
    },
    getCloneTractDetail: function* () {
        yield takeEvery(actionTypes.GET_CLONE_TRACT_DETAIL, sagas.getTractDetail);
    },
    getTownships: function* () {
        yield takeEvery(actionTypes.GET_TOWNSHIPS, sagas.getTownships);
    },
    getEasternTownships: function* () {
        yield takeEvery(actionTypes.GET_EASTERN_TOWNSHIPS, sagas.getEasternTownships);
    },
    getTownship: function* () {
        yield takeEvery(actionTypes.GET_TOWNSHIP, sagas.getTownship);
    },
    getEasternTownship: function* () {
        yield takeEvery(actionTypes.GET_EASTERN_TOWNSHIP, sagas.getEasternTownship);
    },
    getSections: function* () {
        yield takeEvery(actionTypes.GET_SECTIONS, sagas.getSections);
    },
    getMap: function* () {
        yield takeEvery(actionTypes.GET_EASTERN_MAP, sagas.getMap);
    },
    getMeridians: function* () {
        yield takeEvery(actionTypes.GET_MERIDIANS, sagas.getMeridians);
    },
    selectTownship: function* () {
        yield takeEvery(actionTypes.SELECT_TOWNSHIP, sagas.selectTownship);
    },
    createTownship: function* () {
        yield takeEvery(actionTypes.CREATE_TOWNSHIP, sagas.createTownship);
    },
    createEasternTownship: function* () {
        yield takeEvery(actionTypes.CREATE_EASTERN_TOWNSHIP, sagas.createEasternTownship);
    },
    createSection: function* () {
        yield takeEvery(actionTypes.CREATE_SECTION, sagas.createSection);
    },
    updateSection: function* () {
        yield takeEvery(actionTypes.UPDATE_SECTION, sagas.updateSection);
    },
    updateTownship: function* () {
        yield takeEvery(actionTypes.UPDATE_TOWNSHIP, sagas.updateTownship);
    },
    saveTractDetail: function* () {
        yield takeEvery(actionTypes.SAVE_TRACT_DETAIL, sagas.saveTractDetail);
    },
    getOwnershipDetail: function* () {
        yield takeEvery(actionTypes.GET_OWNERSHIP_DETAIL, sagas.getOwnershipDetail);
    },
    getTracts: function* () {
        yield takeEvery(actionTypes.GET_TRACTS, sagas.getTracts);
    },
    getCloneTracts: function* () {
        yield takeEvery(actionTypes.GET_CLONE_TRACTS, sagas.getCloneTracts);
    },
    getCloneSections: function* () {
        yield takeEvery(actionTypes.GET_CLONE_SECTIONS, sagas.getCloneSections);
    },
    getCloneTownships: function* () {
        yield takeEvery(actionTypes.GET_CLONE_TOWNSHIPS, sagas.getCloneTownships);
    },
    selectSection: function* () {
        yield takeEvery(actionTypes.SELECT_SECTION, sagas.selectSection);
    },
    createHorizon: function* () {
        yield takeEvery(actionTypes.CREATE_HORIZON, sagas.createHorizon);
    },
    saveExaminedHistory: function* () {
        yield takeEvery(actionTypes.SAVE_HORIZON_EXAMINED_HISTORY, sagas.saveExaminedHistory);
    },
    deleteOwnershipDetail: function* () {
        yield takeEvery(actionTypes.DELETE_OWNERSHIP_DETAIL, sagas.deleteOwnershipDetail);
    },
    saveAddOwnershipCollection: function* () {
        yield takeEvery(actionTypes.SAVE_ADD_OWNERSHIP_COLLECTION, sagas.saveAddOwnershipCollection);
    },
    createTract: function* () {
        yield takeEvery(actionTypes.CREATE_TRACT, sagas.createTract);
    },
    deleteTract: function* () {
        yield takeEvery(actionTypes.DELETE_TRACT, sagas.deleteTract);
    },
    deleteSection: function* () {
        yield takeEvery(actionTypes.DELETE_SECTION, sagas.deleteSection);
    },
    deleteTownship: function* () {
        yield takeEvery(actionTypes.DELETE_TOWNSHIP, sagas.deleteTownship);
    },
    getSearchPagedOwners: function* () {
        yield takeEvery(actionTypes.GET_SEARCH_PAGED_OWNERS, sagas.getSearchPagedOwners);
    },
    getSearchOwners: function* () {
        yield takeEvery(actionTypes.GET_SEARCH_OWNERS, sagas.getSearchOwners);
    },
    getSearchDeviationsOwners: function* () {
        yield takeEvery(actionTypes.GET_SEARCH_OWNERS_DEVIATIONS, sagas.getSearchDeviationsOwners);
    },
    getPagedOwnerships: function* () {
        yield takeEvery(actionTypes.GET_PAGED_OWNERSHIPS, sagas.getPagedOwnerships);
    },
    getFormations: function* () {
        yield takeEvery(actionTypes.GET_FORMATIONS, sagas.getFormations);
    },
    createFormation: function* () {
        yield takeEvery(actionTypes.CREATE_FORMATION, sagas.createFormation);
    },
    sequenceHorizons: function* () {
        yield takeEvery(actionTypes.SEQUENCE_HORIZONS, sagas.sequenceHorizons);
    },
    getUniqueHorizonIntervals: function* () {
        yield takeEvery(actionTypes.GET_UNIQUE_HORIZONS, sagas.getUniqueHorizonIntervals);
    },
    saveAddOwnership: function* () {
        yield takeEvery(actionTypes.SAVE_ADD_OWNERSHIP, sagas.saveAddOwnership);
    },
    getTractOwners: function* () {
        yield takeEvery(actionTypes.GET_TRACT_OWNERS, sagas.getTractOwners);
    },
    updateTractNote: function* () {
        yield takeEvery(actionTypes.UPDATE_TRACT_NOTE, sagas.updateTractNote);
    },
    updateOwnerNote: function* () {
        yield takeEvery(actionTypes.UPDATE_OWNER_NOTE, sagas.updateOwnerNote);
    },
    updateHorizonInterval: function* () {
        yield takeEvery(actionTypes.UPDATE_HORIZON_INTERVAL, sagas.updateHorizonInterval);
    },
    getTownshipSectionsForSectionNumber: function* () {
        yield takeEvery(actionTypes.GET_TOWNSHIP_SECTIONS_FOR_SECTION, sagas.getTownshipSectionsForSectionNumber);
    },
    deleteOwnership: function* () {
        yield takeEvery(actionTypes.DELETE_OWNERSHIP, sagas.deleteOwnership);
    },
    incrementOwnershipPage: function* () {
        yield takeEvery(actionTypes.INCREMENT_OWNERSHIP_PAGE, sagas.incrementOwnership);
    },
    incrementOwnersPage: function* () {
        yield takeEvery(actionTypes.INCREMENT_OWNERS_PAGE, sagas.incrementOwners);
    },
    deleteHorizon: function* () {
        yield takeEvery(actionTypes.DELETE_HORIZON, sagas.deleteHorizon);
    },
    promoteAdditionalOwnership: function* () {
        yield takeEvery(actionTypes.PROMOTE_ADDITIONAL_OWNERSHIP, sagas.promoteAdditionalOwnership);
    },

    getTractsByOwner: function* () {
        yield takeEvery(actionTypes.GET_TRACTS_BY_OWNER, sagas.getTractsByOwner);
    },
    getSurfaceTractsByOwner: function* () {
        yield takeEvery(actionTypes.GET_SURFACE_TRACTS_BY_OWNER, sagas.getSurfaceTractsByOwner);
    },
    createEasternMap: function* () {
        yield takeEvery(actionTypes.CREATE_EASTERN_MAP, sagas.createEasternMap);
    },
};

export function* saga() {
    yield fork(watches.getCityState);
    yield fork(watches.getTractsByOwner);
    yield fork(watches.getSurfaceTractsByOwner);
    yield fork(watches.getTractDetail);
    yield fork(watches.getCloneTractDetail);
    yield fork(watches.getTownships);
    yield fork(watches.getSections);
    yield fork(watches.getMeridians);
    yield fork(watches.selectTownship);
    yield fork(watches.createTownship);
    yield fork(watches.createEasternTownship);
    yield fork(watches.updateTownship);
    yield fork(watches.createSection);
    yield fork(watches.updateSection);
    yield fork(watches.saveTractDetail);
    yield fork(watches.getOwnershipDetail);
    yield fork(watches.getTracts);
    yield fork(watches.deleteTownship);
    yield fork(watches.deleteSection);
    yield fork(watches.deleteTract);
    yield fork(watches.getCloneTracts);
    yield fork(watches.getCloneSections);
    yield fork(watches.getCloneTownships);
    yield fork(watches.selectSection);
    yield fork(watches.createHorizon);
    yield fork(watches.saveExaminedHistory);
    yield fork(watches.deleteOwnershipDetail);
    yield fork(watches.saveAddOwnershipCollection);
    yield fork(watches.createTract);
    yield fork(watches.deleteTract);
    yield fork(watches.getSearchPagedOwners);
    yield fork(watches.getSearchOwners);
    yield fork(watches.getSearchDeviationsOwners);
    yield fork(watches.getPagedOwnerships);
    yield fork(watches.getFormations);
    yield fork(watches.createFormation);
    yield fork(watches.sequenceHorizons);
    yield fork(watches.getUniqueHorizonIntervals);
    yield fork(watches.saveAddOwnership);
    yield fork(watches.getTractOwners);
    yield fork(watches.updateTractNote);
    yield fork(watches.updateOwnerNote);
    yield fork(watches.updateHorizonInterval);
    yield fork(watches.getTownshipSectionsForSectionNumber);
    yield fork(watches.deleteOwnership);
    yield fork(watches.incrementOwnershipPage);
    yield fork(watches.incrementOwnersPage);
    yield fork(watches.deleteHorizon);
    yield fork(watches.promoteAdditionalOwnership);
    yield fork(watches.getEasternTownships);
    yield fork(watches.getMap);
    yield fork(watches.createEasternMap);
    yield fork(watches.getTownship);
    yield fork(watches.getEasternTownship);
}


// WEBPACK FOOTER //
// ./src/reducers/Tract.ts